import React, {useEffect, useMemo, useState} from 'react';
import {useSelector} from 'react-redux';

import type {RootState} from 'appredux/reducers';
import {
  apiCreateFdCustomerSupplier,
  apiCreateFdFarmerSupplier,
  apiCreateFdFarmTeam,
  apiCreateOrUpdateFdApplicationProperties,
  apiCreateFdFarmRisk,
  apiCreateFdApplicationHistoryCommodities,
  apiDeleteCustomerSupplier,
  apiDeleteFarmerSupplier,
  apiDeleteFdFarmTeam,
  apiDeleteFdApplicationProperty,
  apiDeleteFdFarmRisk,
  apiGetAllFarmTeamRoles,
  apiGetAllFdCustomers,
  apiGetAllFdCrops,
  apiGetAllFdSuppliers,
  apiGetAllFdCommodityTypes,
  apiGetFarmTeamsByApplicationId,
  apiGetAllFarmRisks,
  apiGetCustomerSuppliersByApplicationId,
  apiGetFarmRisksByApplicationId,
  apiGetFdApplicationPropertiesByApplicationId,
  apiGetFdApplicationWaterRightsByApplicationId,
  apiGetFarmerSuppliersByApplicationId,
  apiGetFdApplicationHistoryCommoditiesByApplicationId,
} from 'constants/Api';

import ApplicationJourneyStepLayout from './ApplicationJourneyStepLayout';
import ApplicationJourneySubStepProgress from './ApplicationJourneySubStepProgress';
import {useSupplierApplicationJourney} from './SupplierApplicationJourneyContext';
import {
  lshUseCommoditySelectionSubStep,
  lshUseFarmOverviewSubStep,
  lshUseWaterRightsSubStep,
} from './steps/farm-information/hooks';
import {
  CommoditySelectionSubStep,
  FarmOverviewSubStep,
  PrimaryCustomersSubStep,
  PrimarySuppliersSubStep,
  PropertySubStep,
  TeamSubStep,
  TopRisksSubStep,
  WaterRightsSubStep,
} from './steps/farm-information/substeps';
import {
  EMPTY_WATER_RIGHT_FORM,
  FARM_OVERVIEW_INITIAL_VALIDATION,
  FARM_OVERVIEW_LEASE_YEAR_OPTIONS,
  FARM_OVERVIEW_WATER_SOURCE_OPTIONS,
  FARM_OVERVIEW_YES_NO_OPTIONS,
  lshNormaliseNumericInput,
  lshResolveFarmTypeOption,
  lshResolveLeaseEndYear,
  lshResolveOwnershipOption,
  lshResolveWaterSources,
  WATER_RIGHTS_SCALE_OPTIONS,
  WATER_RIGHTS_SOURCE_OPTIONS,
  WATER_RIGHTS_YES_NO_OPTIONS,
} from './steps/farm-information/farmInformation.utils';

type FarmSubStepKey =
  | 'commodity-selection'
  | 'farm-overview'
  | 'water-rights'
  | 'property'
  | 'top-risks'
  | 'primary-customers'
  | 'primary-suppliers'
  | 'team';

type OwnershipOption = 'Leased' | 'Owned' | 'Both' | '';
type FarmTypeOption = 'Irrigated' | 'Dryland' | 'Both' | '';

type ICommodityType = {
  id: number;
  name: string;
};

type IPrimaryCustomerOption = {
  id: number;
  name: string;
};

type IPrimaryCustomerSelection = {
  id?: number | null;
  applicationId: number;
  customerId: number;
  cropId: number;
};

type IPrimaryCustomerDeletionItem = {
  customerId: number;
  cropId: number;
};

type IPrimarySupplierProduct = {
  id: number;
  name: string;
};

type IPrimarySupplierOption = {
  id: number;
  name: string;
  products: IPrimarySupplierProduct[];
};

type IPrimarySupplierSelection = {
  id?: number | null;
  applicationId: number;
  supplierId: number;
  supplierProductId: number;
};

type IPrimarySupplierDeletionItem = {
  supplierId: number;
  supplierProductId: number;
};

type ITeamRoleOption = {
  id: number;
  name: string;
};

type ITeamMemberRecord = {
  id: number;
  name: string;
  description?: string | null;
  farmTeamRole: {
    id: number;
    name: string;
  } | null;
};

type ITeamModalState = {
  isOpen: boolean;
  mode: 'create' | 'edit';
  memberId: number | null;
  name: string;
  roleId: number | '';
  description: string;
  error: string | null;
};

type IFarmOverviewValidationState = {
  farmName: boolean;
  farmBackground: boolean;
  farmDirections: boolean;
  farmOwnership: boolean;
  areaLeased: boolean;
  leaseEndDate: boolean;
  ownedHectares: boolean;
  farmType: boolean;
  areaUnderIrrigation: boolean;
  areaOfDryLand: boolean;
  waterRights: boolean;
  fullTimeFarming: boolean;
  shortTermGoals: boolean;
  longTermGoals: boolean;
  landComposition: boolean;
  workforceNumber: boolean;
  workerTrust: boolean;
  employmentBeforeAfter: boolean;
};

type IApplicationWaterRightRecord = {
  id?: number;
  scheme?: string | null;
  schedule?: string | null;
  waterSource?: string[] | null;
  waterRights?: boolean | null;
  waterScale?: string | null;
  permitNumber?: string | null;
  controlled?: boolean | null;
  notes?: string | null;
  householdConsumption?: string[] | null;
  waterQuantity?: number | null;
};

type IWaterRightFormState = {
  id?: number;
  waterSource: string;
  householdConsumption: string;
  scheme: string;
  permitNumber: string;
  schedule: string;
  waterScale: string;
  waterQuantity: string;
  controlled: 'yes' | 'no';
  notes: string;
};

type IApplicationPropertyRecord = {
  id?: number;
  nameOfDeed?: string | null;
  areaDistrict?: string | null;
  farmArrangement?: string | null;
  term?: number | null;
  amount?: number | null;
  dryland?: number | null;
  irrigation?: number | null;
  pastures?: number | null;
  grazing?: number | null;
  intensive?: number | null;
  other?: number | null;
  notes?: string | null;
  total?: number | null;
};

type IPropertyFormState = {
  id?: number;
  nameOfDeed: string;
  areaDistrict: string;
  farmArrangement: string;
  term: string;
  amount: string;
  dryland: string;
  irrigation: string;
  pastures: string;
  grazing: string;
  intensive: string;
  other: string;
  notes: string;
};

type IFarmRiskCatalogueItem = {
  id: number;
  name: string;
};

type IFarmRiskSelection = {
  id?: number | null;
  applicationId: number;
  riskId: number;
  risk: IFarmRiskCatalogueItem;
  problem: string;
  mitigation: string;
};

type IRiskDraft = {
  problem: string;
  mitigation: string;
};

function lshToText(value: unknown, fallback = 'Not provided'): string {
  if (value === null || value === undefined) return fallback;
  const text = String(value).trim();
  return text || fallback;
}

const EMPTY_PROPERTY_FORM: IPropertyFormState = {
  nameOfDeed: '',
  areaDistrict: '',
  farmArrangement: '',
  term: '',
  amount: '',
  dryland: '',
  irrigation: '',
  pastures: '',
  grazing: '',
  intensive: '',
  other: '',
  notes: '',
};

const TEAM_NAME_REQUIRED_ERROR = 'Please provide the team member name.';
const TEAM_ROLE_REQUIRED_ERROR = 'Please choose the team member role.';
const TEAM_ROLE_NOT_AVAILABLE_ERROR = 'Selected role is no longer available.';

const TEAM_MODAL_INITIAL_STATE: ITeamModalState = {
  isOpen: false,
  mode: 'create',
  memberId: null,
  name: '',
  roleId: '',
  description: '',
  error: null,
};

function lshToNumberOrNull(value: string): number | null {
  if (!value.trim()) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function lshToIntOrNull(value: string): number | null {
  if (!value.trim()) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.trunc(parsed) : null;
}

function lshNormalizeFarmArrangement(value: string): 'OWNED' | 'LEASED' | '' {
  const normalized = value.trim().toUpperCase();
  if (normalized === 'OWNED') return 'OWNED';
  if (normalized === 'LEASED') return 'LEASED';
  return '';
}

function lshEmptyRiskDraft(): IRiskDraft {
  return {
    problem: '',
    mitigation: '',
  };
}

const ApplicationFarmInformationStep: React.FC = () => {
  const {
    application,
    activeStep,
    activeSubStepIndex,
    setApplication,
    refreshJourney,
    goPrevious,
    goNext,
    goToSubStep,
  } = useSupplierApplicationJourney();

  const editState = useSelector(
    (state: RootState) => state.sidepanel?.editState,
  );
  const [isLoadingPrimaryCustomers, setIsLoadingPrimaryCustomers] =
    useState(false);
  const [isSavingPrimaryCustomers, setIsSavingPrimaryCustomers] =
    useState(false);
  const [primaryCustomersError, setPrimaryCustomersError] = useState<
    string | null
  >(null);
  const [primaryCustomers, setPrimaryCustomers] = useState<
    IPrimaryCustomerOption[]
  >([]);
  const [primaryCustomerCrops, setPrimaryCustomerCrops] = useState<
    IPrimaryCustomerOption[]
  >([]);
  const [primaryCustomerSelections, setPrimaryCustomerSelections] = useState<
    IPrimaryCustomerSelection[]
  >([]);
  const [primaryCustomerDeleteQueue, setPrimaryCustomerDeleteQueue] = useState<
    IPrimaryCustomerDeletionItem[]
  >([]);
  const [expandedPrimaryCustomerId, setExpandedPrimaryCustomerId] = useState<
    number | null
  >(null);
  const [isLoadingPrimarySuppliers, setIsLoadingPrimarySuppliers] =
    useState(false);
  const [isSavingPrimarySuppliers, setIsSavingPrimarySuppliers] =
    useState(false);
  const [primarySuppliersError, setPrimarySuppliersError] = useState<
    string | null
  >(null);
  const [primarySuppliers, setPrimarySuppliers] = useState<
    IPrimarySupplierOption[]
  >([]);
  const [primarySupplierSelections, setPrimarySupplierSelections] = useState<
    IPrimarySupplierSelection[]
  >([]);
  const [primarySupplierDeleteQueue, setPrimarySupplierDeleteQueue] = useState<
    IPrimarySupplierDeletionItem[]
  >([]);
  const [expandedPrimarySupplierId, setExpandedPrimarySupplierId] = useState<
    number | null
  >(null);
  const [isLoadingTeam, setIsLoadingTeam] = useState(false);
  const [isSavingTeam, setIsSavingTeam] = useState(false);
  const [teamError, setTeamError] = useState<string | null>(null);
  const [teamRoles, setTeamRoles] = useState<ITeamRoleOption[]>([]);
  const [teamMembers, setTeamMembers] = useState<ITeamMemberRecord[]>([]);
  const [teamDeleteLoading, setTeamDeleteLoading] = useState<
    Record<number, boolean>
  >({});
  const [pendingTeamDeleteId, setPendingTeamDeleteId] = useState<number | null>(
    null,
  );
  const [teamTempIdCounter, setTeamTempIdCounter] = useState(-1);
  const [teamModalState, setTeamModalState] = useState<ITeamModalState>(
    TEAM_MODAL_INITIAL_STATE,
  );
  const [commodityTypes, setCommodityTypes] = useState<ICommodityType[]>([]);
  const [selectedCommodityIds, setSelectedCommodityIds] = useState<number[]>(
    [],
  );
  const [isLoadingCommodities, setIsLoadingCommodities] = useState(false);
  const [isSavingCommodities, setIsSavingCommodities] = useState(false);
  const [commodityError, setCommodityError] = useState<string | null>(null);
  const [farmOverviewError, setFarmOverviewError] = useState<string | null>(
    null,
  );
  const [isSavingFarmOverview, setIsSavingFarmOverview] = useState(false);
  const [farmName, setFarmName] = useState('');
  const [farmBackground, setFarmBackground] = useState('');
  const [farmDirections, setFarmDirections] = useState('');
  const [farmOwnership, setFarmOwnership] = useState<OwnershipOption>('');
  const [areaLeased, setAreaLeased] = useState('');
  const [leaseEndDate, setLeaseEndDate] = useState('');
  const [ownedHectares, setOwnedHectares] = useState('');
  const [farmType, setFarmType] = useState<FarmTypeOption>('');
  const [areaUnderIrrigation, setAreaUnderIrrigation] = useState('');
  const [areaOfDryLand, setAreaOfDryLand] = useState('');
  const [selectedWaterSources, setSelectedWaterSources] = useState<string[]>(
    [],
  );
  const [waterRights, setWaterRights] = useState('');
  const [fullTimeFarming, setFullTimeFarming] = useState('');
  const [shortTermGoals, setShortTermGoals] = useState('');
  const [longTermGoals, setLongTermGoals] = useState('');
  const [landComposition, setLandComposition] = useState('');
  const [workforceNumber, setWorkforceNumber] = useState('');
  const [workerTrust, setWorkerTrust] = useState('');
  const [employmentBeforeAfter, setEmploymentBeforeAfter] = useState('');
  const [farmValidationErrors, setFarmValidationErrors] =
    useState<IFarmOverviewValidationState>(FARM_OVERVIEW_INITIAL_VALIDATION);
  const [isLoadingWaterRights, setIsLoadingWaterRights] = useState(false);
  const [isSavingWaterRights, setIsSavingWaterRights] = useState(false);
  const [hasWaterRightsStepValue, setHasWaterRightsStepValue] = useState(false);
  const [waterRightRecords, setWaterRightRecords] = useState<
    IApplicationWaterRightRecord[]
  >([]);
  const [waterRightForm, setWaterRightForm] = useState<IWaterRightFormState>(
    EMPTY_WATER_RIGHT_FORM,
  );
  const [waterRightsError, setWaterRightsError] = useState<string | null>(null);
  const [isLoadingProperties, setIsLoadingProperties] = useState(false);
  const [isSavingProperties, setIsSavingProperties] = useState(false);
  const [propertyRecords, setPropertyRecords] = useState<
    IApplicationPropertyRecord[]
  >([]);
  const [propertyForm, setPropertyForm] =
    useState<IPropertyFormState>(EMPTY_PROPERTY_FORM);
  const [propertyError, setPropertyError] = useState<string | null>(null);
  const [isLoadingTopRisks, setIsLoadingTopRisks] = useState(false);
  const [hasAttemptedTopRisksBootstrap, setHasAttemptedTopRisksBootstrap] =
    useState(false);
  const [topRisksError, setTopRisksError] = useState<string | null>(null);
  const [riskCatalogue, setRiskCatalogue] = useState<IFarmRiskCatalogueItem[]>(
    [],
  );
  const [riskSelections, setRiskSelections] = useState<IFarmRiskSelection[]>(
    [],
  );
  const [riskDrafts, setRiskDrafts] = useState<Record<number, IRiskDraft>>({});
  const [riskErrors, setRiskErrors] = useState<Record<number, string>>({});
  const [confirmDeleteByRiskId, setConfirmDeleteByRiskId] = useState<
    Record<number, boolean>
  >({});
  const [perRiskSaving, setPerRiskSaving] = useState<Record<number, boolean>>(
    {},
  );

  useEffect(() => {
    if (!application?.id) {
      setPrimarySuppliers([]);
      setPrimarySupplierSelections([]);
      setPrimarySupplierDeleteQueue([]);
      setExpandedPrimarySupplierId(null);
      return;
    }

    let isMounted = true;
    setIsLoadingPrimarySuppliers(true);
    setPrimarySuppliersError(null);

    Promise.all([
      apiGetAllFdSuppliers(),
      apiGetFarmerSuppliersByApplicationId(application.id),
    ])
      .then(([supplierRows, selectionRows]) => {
        if (!isMounted) return;

        const mappedSuppliers = (
          Array.isArray(supplierRows) ? supplierRows : []
        )
          .map<IPrimarySupplierOption | null>((row) => {
            const id = Number(row?.id);
            if (!Number.isFinite(id)) return null;

            const supplierProducts = Array.isArray((row as any)?.products)
              ? ((row as any).products as Array<{
                  id?: number | string;
                  name?: string;
                }>)
              : [];

            const products = supplierProducts
              .map<IPrimarySupplierProduct | null>((product) => {
                const productId = Number(product?.id);
                if (!Number.isFinite(productId)) return null;
                return {
                  id: productId,
                  name:
                    String(product?.name || '').trim() ||
                    `Product ${productId}`,
                };
              })
              .filter((product): product is IPrimarySupplierProduct =>
                Boolean(product),
              );

            return {
              id,
              name: String(row?.name || '').trim() || `Supplier ${id}`,
              products,
            };
          })
          .filter((supplier): supplier is IPrimarySupplierOption =>
            Boolean(supplier),
          );

        const mappedSelections = (
          Array.isArray(selectionRows) ? selectionRows : []
        )
          .map<IPrimarySupplierSelection | null>((row) => {
            const supplierId = Number((row as any)?.supplierList?.id);
            const supplierProductId = Number(
              (row as any)?.supplierProductList?.id,
            );
            if (
              !Number.isFinite(supplierId) ||
              !Number.isFinite(supplierProductId)
            ) {
              return null;
            }

            return {
              id:
                row?.id === null || row?.id === undefined
                  ? null
                  : Number(row.id),
              applicationId: Number(application.id),
              supplierId,
              supplierProductId,
            };
          })
          .filter((selection): selection is IPrimarySupplierSelection =>
            Boolean(selection),
          );

        setPrimarySuppliers(mappedSuppliers);
        setPrimarySupplierSelections(mappedSelections);
        setPrimarySupplierDeleteQueue([]);
      })
      .catch((error) => {
        if (!isMounted) return;

        const fallbackSelections = (application?.suppliers || [])
          .map<IPrimarySupplierSelection | null>((row: any) => {
            const supplierId = Number(row?.supplierList?.id ?? row?.id);
            const supplierProductId = Number(row?.supplierProductList?.id);
            if (
              !Number.isFinite(supplierId) ||
              !Number.isFinite(supplierProductId)
            ) {
              return null;
            }

            return {
              id:
                row?.id === null || row?.id === undefined
                  ? null
                  : Number(row.id),
              applicationId: Number(application.id),
              supplierId,
              supplierProductId,
            };
          })
          .filter((selection): selection is IPrimarySupplierSelection =>
            Boolean(selection),
          );

        setPrimarySupplierSelections(fallbackSelections);
        setPrimarySuppliersError(
          'Could not load suppliers at the moment. Please try again.',
        );
        console.error(
          '[ApplicationFarmInformationStep] Failed to load primary suppliers',
          error,
        );
      })
      .finally(() => {
        if (!isMounted) return;
        setIsLoadingPrimarySuppliers(false);
      });

    return () => {
      isMounted = false;
    };
  }, [application?.id, application?.suppliers, editState]);

  useEffect(() => {
    if (!application?.id) {
      setTeamRoles([]);
      setTeamMembers([]);
      setPendingTeamDeleteId(null);
      setTeamModalState(TEAM_MODAL_INITIAL_STATE);
      return;
    }

    let isMounted = true;
    setIsLoadingTeam(true);
    setTeamError(null);

    Promise.all([
      apiGetAllFarmTeamRoles(),
      apiGetFarmTeamsByApplicationId(application.id),
    ])
      .then(([roleRows, memberRows]) => {
        if (!isMounted) return;

        const mappedRoles = (Array.isArray(roleRows) ? roleRows : [])
          .map<ITeamRoleOption | null>((row) => {
            const id = Number(row?.id);
            if (!Number.isFinite(id)) return null;
            return {
              id,
              name: String(row?.name || '').trim() || `Role ${id}`,
            };
          })
          .filter((role): role is ITeamRoleOption => Boolean(role));

        const mappedMembers = (Array.isArray(memberRows) ? memberRows : [])
          .map<ITeamMemberRecord | null>((member: any) => {
            const id = Number(member?.id);
            const roleId = Number(member?.farmTeamRole?.id);
            const roleName = String(member?.farmTeamRole?.name || '').trim();

            if (!Number.isFinite(id)) return null;

            return {
              id,
              name: String(member?.name || '').trim() || `Member ${id}`,
              description: String(member?.description || '').trim(),
              farmTeamRole: Number.isFinite(roleId)
                ? {
                    id: roleId,
                    name: roleName || `Role ${roleId}`,
                  }
                : null,
            };
          })
          .filter((member): member is ITeamMemberRecord => Boolean(member));

        setTeamRoles(mappedRoles);
        setTeamMembers(mappedMembers);
        setPendingTeamDeleteId(null);
      })
      .catch((error) => {
        if (!isMounted) return;

        const fallbackMembers = (application?.team || [])
          .map<ITeamMemberRecord | null>((member: any) => {
            const id = Number(member?.id);
            const roleId = Number(member?.farmTeamRole?.id);
            if (!Number.isFinite(id)) return null;

            return {
              id,
              name: String(member?.name || '').trim() || `Member ${id}`,
              description: String(member?.description || '').trim(),
              farmTeamRole: Number.isFinite(roleId)
                ? {
                    id: roleId,
                    name:
                      String(member?.farmTeamRole?.name || '').trim() ||
                      `Role ${roleId}`,
                  }
                : null,
            };
          })
          .filter((member): member is ITeamMemberRecord => Boolean(member));

        setTeamMembers(fallbackMembers);
        setTeamError(
          'Could not load your team at the moment. Please try again.',
        );
        console.error(
          '[ApplicationFarmInformationStep] Failed to load farm team',
          error,
        );
      })
      .finally(() => {
        if (!isMounted) return;
        setIsLoadingTeam(false);
      });

    return () => {
      isMounted = false;
    };
  }, [application?.id, application?.team, editState]);

  useEffect(() => {
    if (!application?.id) {
      setPrimaryCustomers([]);
      setPrimaryCustomerCrops([]);
      setPrimaryCustomerSelections([]);
      setPrimaryCustomerDeleteQueue([]);
      setExpandedPrimaryCustomerId(null);
      return;
    }

    let isMounted = true;
    setIsLoadingPrimaryCustomers(true);
    setPrimaryCustomersError(null);

    Promise.all([
      apiGetAllFdCustomers(),
      apiGetAllFdCrops(),
      apiGetCustomerSuppliersByApplicationId(application.id),
    ])
      .then(([customerRows, cropRows, selectionRows]) => {
        if (!isMounted) return;

        const mappedCustomers = (
          Array.isArray(customerRows) ? customerRows : []
        )
          .map<IPrimaryCustomerOption | null>((row) => {
            const id = Number(row?.id);
            if (!Number.isFinite(id)) return null;
            return {
              id,
              name: String(row?.name || '').trim() || `Customer ${id}`,
            };
          })
          .filter((row): row is IPrimaryCustomerOption => Boolean(row));

        const mappedCrops = (Array.isArray(cropRows) ? cropRows : [])
          .map<IPrimaryCustomerOption | null>((row) => {
            const id = Number(row?.id);
            if (!Number.isFinite(id)) return null;
            return {
              id,
              name: String(row?.name || '').trim() || `Crop ${id}`,
            };
          })
          .filter((row): row is IPrimaryCustomerOption => Boolean(row));

        const mappedSelections = (
          Array.isArray(selectionRows) ? selectionRows : []
        )
          .map<IPrimaryCustomerSelection | null>((row) => {
            const customerId = Number(row?.customer?.id);
            const cropId = Number(row?.crop?.id);
            if (!Number.isFinite(customerId) || !Number.isFinite(cropId)) {
              return null;
            }

            return {
              id:
                row?.id === null || row?.id === undefined
                  ? null
                  : Number(row.id),
              applicationId: Number(application.id),
              customerId,
              cropId,
            };
          })
          .filter((row): row is IPrimaryCustomerSelection => Boolean(row));

        setPrimaryCustomers(mappedCustomers);
        setPrimaryCustomerCrops(mappedCrops);
        setPrimaryCustomerSelections(mappedSelections);
        setPrimaryCustomerDeleteQueue([]);
      })
      .catch((error) => {
        if (!isMounted) return;

        const fallbackSelections = (application?.customers || [])
          .map<IPrimaryCustomerSelection | null>((row) => {
            const customerId = Number((row as any)?.customer?.id);
            const cropId = Number((row as any)?.crop?.id);
            if (!Number.isFinite(customerId) || !Number.isFinite(cropId)) {
              return null;
            }

            return {
              id:
                row?.id === null || row?.id === undefined
                  ? null
                  : Number(row.id),
              applicationId: Number(application.id),
              customerId,
              cropId,
            };
          })
          .filter((row): row is IPrimaryCustomerSelection => Boolean(row));

        setPrimaryCustomerSelections(fallbackSelections);
        setPrimaryCustomersError(
          'Could not load customers at the moment. Please try again.',
        );
        console.error(
          '[ApplicationFarmInformationStep] Failed to load primary customers',
          error,
        );
      })
      .finally(() => {
        if (!isMounted) return;
        setIsLoadingPrimaryCustomers(false);
      });

    return () => {
      isMounted = false;
    };
  }, [application?.id, application?.customers, editState]);

  useEffect(() => {
    if (!application?.id) {
      setCommodityTypes([]);
      setSelectedCommodityIds([]);
      return;
    }

    let isMounted = true;
    setIsLoadingCommodities(true);
    setCommodityError(null);

    Promise.all([
      apiGetAllFdCommodityTypes(),
      apiGetFdApplicationHistoryCommoditiesByApplicationId(application.id),
    ])
      .then(([catalogue, selected]) => {
        if (!isMounted) return;

        setCommodityTypes(Array.isArray(catalogue) ? catalogue : []);
        setSelectedCommodityIds(
          (Array.isArray(selected) ? selected : [])
            .map((row) => Number(row?.commodityType?.id))
            .filter((id) => Number.isFinite(id)),
        );
      })
      .catch((error) => {
        if (!isMounted) return;
        setCommodityError(
          'Could not load commodity selections at the moment. Please try again.',
        );
        console.error(
          '[ApplicationFarmInformationStep] Failed to load commodity selections',
          error,
        );
      })
      .finally(() => {
        if (!isMounted) return;
        setIsLoadingCommodities(false);
      });

    return () => {
      isMounted = false;
    };
  }, [application?.id, editState]);

  useEffect(() => {
    if (!application?.id) {
      setHasWaterRightsStepValue(false);
      setWaterRightRecords([]);
      setWaterRightForm(EMPTY_WATER_RIGHT_FORM);
      return;
    }

    let isMounted = true;
    setIsLoadingWaterRights(true);
    setWaterRightsError(null);

    apiGetFdApplicationWaterRightsByApplicationId(application.id)
      .then((response) => {
        if (!isMounted) return;

        const list = Array.isArray(response) ? response : [];
        setWaterRightRecords(list);
        setHasWaterRightsStepValue(Boolean(list[0]?.waterRights));
      })
      .catch((error) => {
        if (!isMounted) return;

        const fallback = (application?.applicationWaterRights || []).map(
          (record: any) => ({
            ...record,
            id:
              record?.id === null || record?.id === undefined
                ? undefined
                : Number(record.id),
          }),
        ) as IApplicationWaterRightRecord[];
        setWaterRightRecords(fallback);
        setHasWaterRightsStepValue(Boolean(fallback[0]?.waterRights));
        setWaterRightsError(
          'Could not load water rights at the moment. Please try again.',
        );
        console.error(
          '[ApplicationFarmInformationStep] Failed to load water rights',
          error,
        );
      })
      .finally(() => {
        if (!isMounted) return;
        setIsLoadingWaterRights(false);
      });

    return () => {
      isMounted = false;
    };
  }, [application?.id, application?.applicationWaterRights, editState]);

  useEffect(() => {
    if (!application?.id) {
      setPropertyRecords([]);
      setPropertyForm(EMPTY_PROPERTY_FORM);
      return;
    }

    let isMounted = true;
    setIsLoadingProperties(true);
    setPropertyError(null);

    apiGetFdApplicationPropertiesByApplicationId(application.id)
      .then((response) => {
        if (!isMounted) return;

        const list = Array.isArray(response) ? response : [];
        const mapped = list.map((record) => ({
          ...record,
          id:
            record?.id === null || record?.id === undefined
              ? undefined
              : Number(record.id),
        })) as IApplicationPropertyRecord[];

        setPropertyRecords(mapped);
      })
      .catch((error) => {
        if (!isMounted) return;

        const fallback = (application?.applicationProperties || []).map(
          (record: any) => ({
            ...record,
            id:
              record?.id === null || record?.id === undefined
                ? undefined
                : Number(record.id),
          }),
        ) as IApplicationPropertyRecord[];

        setPropertyRecords(fallback);
        setPropertyError(
          'Could not load property records at the moment. Please try again.',
        );
        console.error(
          '[ApplicationFarmInformationStep] Failed to load property records',
          error,
        );
      })
      .finally(() => {
        if (!isMounted) return;
        setIsLoadingProperties(false);
      });

    return () => {
      isMounted = false;
    };
  }, [application?.id, application?.applicationProperties, editState]);

  useEffect(() => {
    if (!application?.id) {
      setRiskCatalogue([]);
      setRiskSelections([]);
      setRiskDrafts({});
      setRiskErrors({});
      setConfirmDeleteByRiskId({});
      setHasAttemptedTopRisksBootstrap(true);
      return;
    }

    let isMounted = true;
    setIsLoadingTopRisks(true);
    setTopRisksError(null);

    Promise.all([
      apiGetAllFarmRisks(),
      apiGetFarmRisksByApplicationId(Number(application.id)),
    ])
      .then(([catalogueRows, riskRows]) => {
        if (!isMounted) return;

        const resolvedCatalogue = (
          Array.isArray(catalogueRows) ? catalogueRows : []
        ).map((risk) => ({
          id: Number(risk.id),
          name: String(risk.name || '').trim(),
        }));

        const mappedSelections = (Array.isArray(riskRows) ? riskRows : [])
          .map<IFarmRiskSelection | null>((record) => {
            const riskId = Number(record?.risk?.id);
            if (!Number.isFinite(riskId)) return null;

            return {
              id:
                record?.id === null || record?.id === undefined
                  ? null
                  : Number(record.id),
              applicationId: Number(application.id),
              riskId,
              risk: {
                id: riskId,
                name: String(record?.risk?.name || '').trim() || 'Risk',
              },
              problem: String(record?.problem || ''),
              mitigation: String(record?.mitigation || ''),
            };
          })
          .filter((record): record is IFarmRiskSelection => Boolean(record));

        setRiskCatalogue(resolvedCatalogue);
        setRiskSelections(mappedSelections);
      })
      .catch((error) => {
        if (!isMounted) return;

        const fallbackSelections = (application?.farmRisk || [])
          .map<IFarmRiskSelection | null>((record) => {
            const riskId = Number(record?.risk?.id);
            if (!Number.isFinite(riskId)) return null;

            return {
              id:
                record?.id === null || record?.id === undefined
                  ? null
                  : Number(record.id),
              applicationId: Number(application.id),
              riskId,
              risk: {
                id: riskId,
                name: String(record?.risk?.name || '').trim() || 'Risk',
              },
              problem: String(record?.problem || ''),
              mitigation: String(record?.mitigation || ''),
            };
          })
          .filter((record): record is IFarmRiskSelection => Boolean(record));

        setRiskSelections(fallbackSelections);
        setTopRisksError(
          'Could not load risk records at the moment. Please try again.',
        );
        console.error(
          '[ApplicationFarmInformationStep] Failed to load top risks',
          error,
        );
      })
      .finally(() => {
        if (!isMounted) return;
        setIsLoadingTopRisks(false);
        setHasAttemptedTopRisksBootstrap(true);
      });

    return () => {
      isMounted = false;
    };
  }, [application?.id, application?.farmRisk, editState]);

  useEffect(() => {
    const farm = application?.farm;

    setFarmName(lshToText(farm?.name, ''));
    setFarmBackground(lshToText(farm?.description, ''));
    setFarmDirections(
      lshToText((farm as any)?.directions || (farm as any)?.address, ''),
    );
    setFarmOwnership(
      lshResolveOwnershipOption(
        lshToText((farm as any)?.farmArrangement, '') ||
          (farm?.numberOfLeasedHectares && farm?.ownedHectares
            ? 'BOTH'
            : farm?.numberOfLeasedHectares
            ? 'LEASED'
            : farm?.ownedHectares
            ? 'OWNED'
            : ''),
      ),
    );
    setAreaLeased(lshNormaliseNumericInput(farm?.numberOfLeasedHectares));
    setLeaseEndDate(lshResolveLeaseEndYear(farm?.leaseEndDate));
    setOwnedHectares(lshNormaliseNumericInput(farm?.ownedHectares));
    setFarmType(
      lshResolveFarmTypeOption(farm?.irrigatedHectares, farm?.drylandHectares),
    );
    setAreaUnderIrrigation(lshNormaliseNumericInput(farm?.irrigatedHectares));
    setAreaOfDryLand(lshNormaliseNumericInput(farm?.drylandHectares));
    setSelectedWaterSources(lshResolveWaterSources(farm));
    setWaterRights(farm?.waterRights ? 'Yes' : 'No');
    setFullTimeFarming(farm?.fullTimeFarming ? 'Yes' : 'No');
    setShortTermGoals(lshToText(farm?.shortTermGoals, ''));
    setLongTermGoals(lshToText(farm?.longTermGoals, ''));
    setLandComposition(lshToText(farm?.landComposition, ''));
    setWorkforceNumber(lshNormaliseNumericInput(farm?.workforceNumber));
    setWorkerTrust(lshToText(farm?.workerTrust, ''));
    setEmploymentBeforeAfter(lshToText(farm?.employmentBeforeAndAfter, ''));
    setFarmOverviewError(null);
    setFarmValidationErrors(FARM_OVERVIEW_INITIAL_VALIDATION);
  }, [application?.farm]);

  const farmSubSteps = activeStep.subSteps || [];
  const activeSubStep = farmSubSteps[activeSubStepIndex];
  const activeSubStepKey = (activeSubStep?.key ||
    'commodity-selection') as FarmSubStepKey;
  const {selectedCommoditySet, handleToggleCommodity} =
    lshUseCommoditySelectionSubStep({
      selectedCommodityIds,
      isSavingCommodities,
      setCommodityError,
      setSelectedCommodityIds,
    });
  const {
    farmBackgroundWordCount,
    clearFarmValidationError,
    handleWaterSourceToggle,
    handleSaveFarmOverview,
  } = lshUseFarmOverviewSubStep({
    application,
    farmName,
    farmBackground,
    farmDirections,
    farmOwnership,
    areaLeased,
    leaseEndDate,
    ownedHectares,
    farmType,
    areaUnderIrrigation,
    areaOfDryLand,
    selectedWaterSources,
    waterRights,
    fullTimeFarming,
    shortTermGoals,
    longTermGoals,
    landComposition,
    workforceNumber,
    workerTrust,
    employmentBeforeAfter,
    isSavingFarmOverview,
    setIsSavingFarmOverview,
    setFarmOverviewError,
    farmValidationErrors,
    setFarmValidationErrors,
    setSelectedWaterSources,
    setApplication,
    refreshJourney,
  });
  const {
    selectedWaterSource,
    selectedHouseholdSource,
    handleWaterRightFormChange,
    handleAddWaterRightRecord,
    handleRemoveWaterRightRecord,
    handleSaveWaterRightsStep,
  } = lshUseWaterRightsSubStep({
    application,
    hasWaterRightsStepValue,
    waterRightRecords,
    waterRightForm,
    isSavingWaterRights,
    setIsSavingWaterRights,
    setWaterRightsError,
    setWaterRightForm,
    setWaterRightRecords,
    refreshJourney,
  });

  const waterRightsRecords = waterRightRecords;
  const selectionByRiskId = useMemo(() => {
    const map = new Map<number, IFarmRiskSelection>();
    riskSelections.forEach((selection) => {
      map.set(Number(selection.riskId), selection);
    });
    return map;
  }, [riskSelections]);
  const selectedRiskCount = riskSelections.length;
  const anyRiskSaving = useMemo(
    () => Object.values(perRiskSaving).some(Boolean),
    [perRiskSaving],
  );
  const sortedRiskCatalogue = useMemo(
    () =>
      [...riskCatalogue].sort((left, right) =>
        left.name.toLowerCase().localeCompare(right.name.toLowerCase()),
      ),
    [riskCatalogue],
  );

  useEffect(() => {
    setRiskDrafts((current) => {
      let changed = false;
      const next = {...current};

      sortedRiskCatalogue.forEach((risk) => {
        const riskId = Number(risk.id);
        const selection = selectionByRiskId.get(riskId);
        const selectionProblem = selection?.problem ?? '';
        const selectionMitigation = selection?.mitigation ?? '';
        const existing = next[riskId];

        if (!existing) {
          next[riskId] = {
            problem: selectionProblem,
            mitigation: selectionMitigation,
          };
          changed = true;
          return;
        }

        const existingHasContent =
          existing.problem.trim().length > 0 ||
          existing.mitigation.trim().length > 0;
        if (selection && !existingHasContent) {
          next[riskId] = {
            problem: selectionProblem,
            mitigation: selectionMitigation,
          };
          changed = true;
        }
      });

      Object.keys(next).forEach((key) => {
        const riskId = Number(key);
        const existsInCatalogue = sortedRiskCatalogue.some(
          (risk) => Number(risk.id) === riskId,
        );
        if (!existsInCatalogue) {
          delete next[riskId];
          changed = true;
        }
      });

      return changed ? next : current;
    });
  }, [selectionByRiskId, sortedRiskCatalogue]);

  const sortedPrimaryCustomers = useMemo(
    () =>
      [...primaryCustomers].sort((left, right) =>
        left.name.toLowerCase().localeCompare(right.name.toLowerCase()),
      ),
    [primaryCustomers],
  );

  const sortedPrimaryCustomerCrops = useMemo(
    () =>
      [...primaryCustomerCrops].sort((left, right) =>
        left.name.toLowerCase().localeCompare(right.name.toLowerCase()),
      ),
    [primaryCustomerCrops],
  );

  const primaryCustomerSelectionMap = useMemo(() => {
    const map = new Map<number, Set<number>>();

    primaryCustomerSelections.forEach((selection) => {
      const customerId = Number(selection.customerId);
      const cropId = Number(selection.cropId);

      if (!Number.isFinite(customerId) || !Number.isFinite(cropId)) return;

      if (!map.has(customerId)) {
        map.set(customerId, new Set<number>());
      }
      map.get(customerId)?.add(cropId);
    });

    return map;
  }, [primaryCustomerSelections]);

  const totalPrimaryCustomerSelections = primaryCustomerSelections.length;

  const isPrimaryCustomerItemQueuedForDelete = (
    customerId: number,
    cropId: number,
  ): boolean =>
    primaryCustomerDeleteQueue.some(
      (item) =>
        Number(item.customerId) === Number(customerId) &&
        Number(item.cropId) === Number(cropId),
    );

  const sortedPrimarySuppliers = useMemo(
    () =>
      [...primarySuppliers].sort((left, right) =>
        left.name.toLowerCase().localeCompare(right.name.toLowerCase()),
      ),
    [primarySuppliers],
  );

  const primarySupplierSelectionMap = useMemo(() => {
    const map = new Map<number, Set<number>>();

    primarySupplierSelections.forEach((selection) => {
      const supplierId = Number(selection.supplierId);
      const supplierProductId = Number(selection.supplierProductId);

      if (!Number.isFinite(supplierId) || !Number.isFinite(supplierProductId)) {
        return;
      }

      if (!map.has(supplierId)) {
        map.set(supplierId, new Set<number>());
      }
      map.get(supplierId)?.add(supplierProductId);
    });

    return map;
  }, [primarySupplierSelections]);

  const totalPrimarySupplierSelections = primarySupplierSelections.length;

  const isPrimarySupplierItemQueuedForDelete = (
    supplierId: number,
    supplierProductId: number,
  ): boolean =>
    primarySupplierDeleteQueue.some(
      (item) =>
        Number(item.supplierId) === Number(supplierId) &&
        Number(item.supplierProductId) === Number(supplierProductId),
    );

  const preparedTeamMembers = useMemo(() => teamMembers, [teamMembers]);

  const handleTogglePrimaryCustomer = (customerId: number) => {
    setExpandedPrimaryCustomerId((current) =>
      current === customerId ? null : customerId,
    );
  };

  const handleSelectPrimaryCustomerCrop = (
    customerId: number,
    cropId: number,
  ) => {
    if (!application?.id) return;

    setPrimaryCustomerSelections((current) => {
      const exists = current.some(
        (selection) =>
          Number(selection.customerId) === Number(customerId) &&
          Number(selection.cropId) === Number(cropId),
      );
      if (exists) return current;

      return [
        ...current,
        {
          applicationId: Number(application.id),
          customerId,
          cropId,
        },
      ];
    });

    setPrimaryCustomerDeleteQueue((current) =>
      current.filter(
        (item) =>
          !(
            Number(item.customerId) === Number(customerId) &&
            Number(item.cropId) === Number(cropId)
          ),
      ),
    );

    setExpandedPrimaryCustomerId(customerId);
  };

  const handleDeselectPrimaryCustomerCrop = (
    customerId: number,
    cropId: number,
  ) => {
    setPrimaryCustomerSelections((current) =>
      current.filter(
        (selection) =>
          !(
            Number(selection.customerId) === Number(customerId) &&
            Number(selection.cropId) === Number(cropId)
          ),
      ),
    );

    setPrimaryCustomerDeleteQueue((current) => {
      const alreadyQueued = current.some(
        (item) =>
          Number(item.customerId) === Number(customerId) &&
          Number(item.cropId) === Number(cropId),
      );
      if (alreadyQueued) return current;

      return [...current, {customerId, cropId}];
    });
  };

  const handleSavePrimaryCustomersStep = async (): Promise<boolean> => {
    if (!application?.id || isSavingPrimaryCustomers) return false;

    try {
      setIsSavingPrimaryCustomers(true);
      setPrimaryCustomersError(null);

      for (const item of primaryCustomerDeleteQueue) {
        await apiDeleteCustomerSupplier({
          applicationId: Number(application.id),
          customerId: Number(item.customerId),
          cropId: Number(item.cropId),
        });
      }

      await Promise.all(
        primaryCustomerSelections.map((selection) =>
          apiCreateFdCustomerSupplier({
            applicationId: Number(application.id),
            customerId: Number(selection.customerId),
            cropId: Number(selection.cropId),
          }),
        ),
      );

      setPrimaryCustomerDeleteQueue([]);
      await refreshJourney();
      return true;
    } catch (error) {
      console.error(
        '[ApplicationFarmInformationStep] Failed to save primary customers',
        error,
      );
      setPrimaryCustomersError(
        'Could not save primary customers. Please try again.',
      );
      return false;
    } finally {
      setIsSavingPrimaryCustomers(false);
    }
  };

  const handleTogglePrimarySupplier = (supplierId: number) => {
    setExpandedPrimarySupplierId((current) =>
      current === supplierId ? null : supplierId,
    );
  };

  const handleSelectPrimarySupplierProduct = (
    supplierId: number,
    supplierProductId: number,
  ) => {
    if (!application?.id) return;

    setPrimarySupplierSelections((current) => {
      const exists = current.some(
        (selection) =>
          Number(selection.supplierId) === Number(supplierId) &&
          Number(selection.supplierProductId) === Number(supplierProductId),
      );
      if (exists) return current;

      return [
        ...current,
        {
          applicationId: Number(application.id),
          supplierId,
          supplierProductId,
        },
      ];
    });

    setPrimarySupplierDeleteQueue((current) =>
      current.filter(
        (item) =>
          !(
            Number(item.supplierId) === Number(supplierId) &&
            Number(item.supplierProductId) === Number(supplierProductId)
          ),
      ),
    );

    setExpandedPrimarySupplierId(supplierId);
  };

  const handleDeselectPrimarySupplierProduct = (
    supplierId: number,
    supplierProductId: number,
  ) => {
    setPrimarySupplierSelections((current) =>
      current.filter(
        (selection) =>
          !(
            Number(selection.supplierId) === Number(supplierId) &&
            Number(selection.supplierProductId) === Number(supplierProductId)
          ),
      ),
    );

    setPrimarySupplierDeleteQueue((current) => {
      const alreadyQueued = current.some(
        (item) =>
          Number(item.supplierId) === Number(supplierId) &&
          Number(item.supplierProductId) === Number(supplierProductId),
      );
      if (alreadyQueued) return current;

      return [...current, {supplierId, supplierProductId}];
    });
  };

  const handleSavePrimarySuppliersStep = async (): Promise<boolean> => {
    if (!application?.id || isSavingPrimarySuppliers) return false;

    try {
      setIsSavingPrimarySuppliers(true);
      setPrimarySuppliersError(null);

      for (const item of primarySupplierDeleteQueue) {
        await apiDeleteFarmerSupplier({
          applicationId: Number(application.id),
          supplierId: Number(item.supplierId),
          supplierProductId: Number(item.supplierProductId),
        });
      }

      const validSelections = primarySupplierSelections.filter((selection) => {
        const supplierId = Number(selection.supplierId);
        const supplierProductId = Number(selection.supplierProductId);
        return (
          Number.isFinite(supplierId) && Number.isFinite(supplierProductId)
        );
      });

      if (validSelections.length !== primarySupplierSelections.length) {
        setPrimarySuppliersError(
          'Some supplier selections are missing product information. Please review and try again.',
        );
        return false;
      }

      const newSelections = validSelections.filter(
        (selection) => selection.id === null || selection.id === undefined,
      );

      if (newSelections.length > 0) {
        await Promise.all(
          newSelections.map((selection) =>
            apiCreateFdFarmerSupplier({
              applicationId: Number(application.id),
              supplierId: Number(selection.supplierId),
              supplierProductId: Number(selection.supplierProductId),
            }),
          ),
        );

        const selectionRows = await apiGetFarmerSuppliersByApplicationId(
          application.id,
        );

        const mappedSelections = (
          Array.isArray(selectionRows) ? selectionRows : []
        )
          .map<IPrimarySupplierSelection | null>((row) => {
            const supplierId = Number((row as any)?.supplierList?.id);
            const supplierProductId = Number(
              (row as any)?.supplierProductList?.id,
            );
            if (
              !Number.isFinite(supplierId) ||
              !Number.isFinite(supplierProductId)
            ) {
              return null;
            }

            return {
              id:
                row?.id === null || row?.id === undefined
                  ? null
                  : Number(row.id),
              applicationId: Number(application.id),
              supplierId,
              supplierProductId,
            };
          })
          .filter((selection): selection is IPrimarySupplierSelection =>
            Boolean(selection),
          );

        setPrimarySupplierSelections(mappedSelections);
      }

      setPrimarySupplierDeleteQueue([]);
      await refreshJourney();
      return true;
    } catch (error) {
      console.error(
        '[ApplicationFarmInformationStep] Failed to save primary suppliers',
        error,
      );
      setPrimarySuppliersError(
        'Could not save primary suppliers. Please try again.',
      );
      return false;
    } finally {
      setIsSavingPrimarySuppliers(false);
    }
  };

  const closeTeamModal = () => {
    setTeamModalState(TEAM_MODAL_INITIAL_STATE);
  };

  const openTeamModal = (
    mode: 'create' | 'edit',
    member?: ITeamMemberRecord,
  ) => {
    setPendingTeamDeleteId(null);
    setTeamModalState({
      isOpen: true,
      mode,
      memberId: member?.id ?? null,
      name: member?.name ?? '',
      roleId: member?.farmTeamRole?.id ?? '',
      description: member?.description ?? '',
      error: null,
    });
  };

  const handleTeamModalSubmit = () => {
    const trimmedName = teamModalState.name.trim();
    if (!trimmedName) {
      setTeamModalState((current) => ({
        ...current,
        error: TEAM_NAME_REQUIRED_ERROR,
      }));
      return;
    }

    if (!teamModalState.roleId) {
      setTeamModalState((current) => ({
        ...current,
        error: TEAM_ROLE_REQUIRED_ERROR,
      }));
      return;
    }

    const selectedRole = teamRoles.find(
      (role) => Number(role.id) === Number(teamModalState.roleId),
    );
    if (!selectedRole) {
      setTeamModalState((current) => ({
        ...current,
        error: TEAM_ROLE_NOT_AVAILABLE_ERROR,
      }));
      return;
    }

    let memberId = teamModalState.memberId;
    if (memberId === null || Number.isNaN(memberId) || Number(memberId) === 0) {
      setTeamTempIdCounter((current) => current - 1);
      memberId = teamTempIdCounter;
    }

    const preparedMember: ITeamMemberRecord = {
      id: Number(memberId),
      name: trimmedName,
      description: teamModalState.description.trim() || '',
      farmTeamRole: {
        id: Number(selectedRole.id),
        name: selectedRole.name,
      },
    };

    setTeamMembers((current) => {
      const existingIndex = current.findIndex(
        (member) => Number(member.id) === Number(preparedMember.id),
      );
      if (existingIndex === -1) {
        return [...current, preparedMember];
      }

      const next = [...current];
      next[existingIndex] = preparedMember;
      return next;
    });

    setTeamError(null);
    closeTeamModal();
  };

  const handleTeamModalNameChange = (value: string) => {
    setTeamModalState((current) => ({
      ...current,
      name: value,
      error: null,
    }));
  };

  const handleTeamModalRoleChange = (value: string) => {
    setTeamModalState((current) => ({
      ...current,
      roleId: value ? Number(value) : '',
      error: null,
    }));
  };

  const handleTeamModalDescriptionChange = (value: string) => {
    setTeamModalState((current) => ({
      ...current,
      description: value,
    }));
  };

  const handleDeleteTeamMember = async (member: ITeamMemberRecord) => {
    const memberId = Number(member.id);

    setTeamDeleteLoading((current) => ({
      ...current,
      [memberId]: true,
    }));

    try {
      if (memberId > 0) {
        await apiDeleteFdFarmTeam(memberId);
      }

      setTeamMembers((current) =>
        current.filter((row) => Number(row.id) !== memberId),
      );
      setPendingTeamDeleteId((current) =>
        current === memberId ? null : current,
      );
      setTeamError(null);
    } catch (error) {
      console.error(
        '[ApplicationFarmInformationStep] Failed to delete team member',
        error,
      );
      setTeamError('Could not delete this team member. Please try again.');
    } finally {
      setTeamDeleteLoading((current) => {
        const next = {...current};
        delete next[memberId];
        return next;
      });
    }
  };

  const requestDeleteTeamMember = (member: ITeamMemberRecord) => {
    const memberId = Number(member.id);
    if (pendingTeamDeleteId === memberId) {
      void handleDeleteTeamMember(member);
      return;
    }
    setPendingTeamDeleteId(memberId);
  };

  const handleSaveTeamStep = async (): Promise<boolean> => {
    if (!application?.id || isSavingTeam) return false;

    try {
      setIsSavingTeam(true);
      setTeamError(null);

      for (const member of preparedTeamMembers) {
        const roleId =
          member?.farmTeamRole?.id !== null &&
          member?.farmTeamRole?.id !== undefined
            ? Number(member.farmTeamRole.id)
            : null;

        if (!roleId) {
          setTeamError(
            `Missing role for ${
              member?.name || 'a team member'
            }. Please update and try again.`,
          );
          return false;
        }

        await apiCreateFdFarmTeam({
          applicationId: Number(application.id),
          farmTeamRoleId: roleId,
          name: String(member.name || '').trim(),
          description: String(member.description || '').trim(),
          ...(Number(member.id) > 0 ? {id: Number(member.id)} : {}),
        });
      }

      await refreshJourney();
      return true;
    } catch (error) {
      console.error(
        '[ApplicationFarmInformationStep] Failed to save team members',
        error,
      );
      setTeamError('Could not save your team. Please try again.');
      return false;
    } finally {
      setIsSavingTeam(false);
    }
  };

  const handlePropertyFormChange =
    (field: keyof IPropertyFormState) =>
    (
      event: React.ChangeEvent<
        HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
      >,
    ) => {
      setPropertyError(null);
      setPropertyForm((current) => {
        const nextValue = event.target.value;
        const next = {
          ...current,
          [field]: nextValue,
        };

        if (field === 'farmArrangement') {
          const arrangement = lshNormalizeFarmArrangement(nextValue);
          if (arrangement === 'OWNED') {
            next.term = '';
            next.amount = '';
          }
        }

        return next;
      });
    };

  const handleAddPropertyRecord = () => {
    const farmArrangement = lshNormalizeFarmArrangement(
      propertyForm.farmArrangement,
    );
    const isOwned = farmArrangement === 'OWNED';

    if (
      !propertyForm.nameOfDeed.trim() ||
      !propertyForm.areaDistrict.trim() ||
      !farmArrangement
    ) {
      setPropertyError(
        'Name of deed, area district and farm arrangement are required.',
      );
      return;
    }

    const dryland = lshToNumberOrNull(propertyForm.dryland);
    const irrigation = lshToNumberOrNull(propertyForm.irrigation);
    const pastures = lshToNumberOrNull(propertyForm.pastures);
    const grazing = lshToNumberOrNull(propertyForm.grazing);
    const intensive = lshToNumberOrNull(propertyForm.intensive);
    const other = lshToNumberOrNull(propertyForm.other);

    const nextRecord: IApplicationPropertyRecord = {
      id: propertyForm.id,
      nameOfDeed: propertyForm.nameOfDeed.trim(),
      areaDistrict: propertyForm.areaDistrict.trim(),
      farmArrangement,
      term: isOwned ? null : lshToIntOrNull(propertyForm.term),
      amount: isOwned ? null : lshToNumberOrNull(propertyForm.amount),
      dryland,
      irrigation,
      pastures,
      grazing,
      intensive,
      other,
      notes: propertyForm.notes.trim() || null,
      total:
        (dryland ?? 0) +
        (irrigation ?? 0) +
        (pastures ?? 0) +
        (grazing ?? 0) +
        (intensive ?? 0) +
        (other ?? 0),
    };

    setPropertyRecords((current) => [...current, nextRecord]);
    setPropertyForm(EMPTY_PROPERTY_FORM);
    setPropertyError(null);
  };

  const handleRemovePropertyRecord = async (
    record: IApplicationPropertyRecord,
  ) => {
    if (!record.id) {
      setPropertyRecords((current) =>
        current.filter((item) => item !== record),
      );
      return;
    }

    try {
      await apiDeleteFdApplicationProperty(record.id);
      setPropertyRecords((current) =>
        current.filter((item) => item.id !== record.id),
      );
    } catch (error) {
      console.error(
        '[ApplicationFarmInformationStep] Failed to remove property record',
        error,
      );
      setPropertyError(
        'Could not remove this property record. Please try again.',
      );
    }
  };

  const handleSavePropertyStep = async (): Promise<boolean> => {
    if (!application?.id || isSavingProperties) return false;

    try {
      setIsSavingProperties(true);
      setPropertyError(null);

      if (propertyRecords.length > 0) {
        const hasInvalidArrangement = propertyRecords.some(
          (record) =>
            !lshNormalizeFarmArrangement(String(record.farmArrangement || '')),
        );
        if (hasInvalidArrangement) {
          setPropertyError(
            'Each property entry must have a valid farm arrangement (Owned or Leased).',
          );
          return false;
        }

        await apiCreateOrUpdateFdApplicationProperties(
          propertyRecords.map((record) => {
            const farmArrangement =
              lshNormalizeFarmArrangement(
                String(record.farmArrangement || ''),
              ) || null;
            const isLeased = farmArrangement === 'LEASED';

            return {
              ...(record.id ? {id: Number(record.id)} : {}),
              applicationId: Number(application.id),
              nameOfDeed: String(record.nameOfDeed || ''),
              areaDistrict: String(record.areaDistrict || ''),
              farmArrangement,
              term:
                isLeased && record.term !== null && record.term !== undefined
                  ? Math.trunc(Number(record.term))
                  : null,
              amount:
                isLeased &&
                record.amount !== null &&
                record.amount !== undefined
                  ? Number(record.amount)
                  : null,
              dryland:
                record.dryland !== null && record.dryland !== undefined
                  ? Number(record.dryland)
                  : 0,
              irrigation:
                record.irrigation !== null && record.irrigation !== undefined
                  ? Number(record.irrigation)
                  : 0,
              pastures:
                record.pastures !== null && record.pastures !== undefined
                  ? Number(record.pastures)
                  : 0,
              grazing:
                record.grazing !== null && record.grazing !== undefined
                  ? Number(record.grazing)
                  : 0,
              intensive:
                record.intensive !== null && record.intensive !== undefined
                  ? Number(record.intensive)
                  : 0,
              other:
                record.other !== null && record.other !== undefined
                  ? Number(record.other)
                  : 0,
              notes: String(record.notes || ''),
            };
          }),
        );
      }

      await refreshJourney();
      return true;
    } catch (error) {
      console.error(
        '[ApplicationFarmInformationStep] Failed to save property records',
        error,
      );
      setPropertyError(
        'Could not save property details for this application. Please try again.',
      );
      return false;
    } finally {
      setIsSavingProperties(false);
    }
  };

  const setRiskSavingState = (riskId: number, saving: boolean) => {
    setPerRiskSaving((current) => {
      const next = {...current};
      if (saving) {
        next[riskId] = true;
      } else {
        delete next[riskId];
      }
      return next;
    });
  };

  const handleRiskDraftChange = (
    riskId: number,
    field: keyof IRiskDraft,
    value: string,
  ) => {
    setRiskDrafts((current) => ({
      ...current,
      [riskId]: {
        ...(current[riskId] ?? lshEmptyRiskDraft()),
        [field]: value,
      },
    }));

    setRiskErrors((current) => {
      if (!current[riskId]) return current;
      const next = {...current};
      delete next[riskId];
      return next;
    });

    setConfirmDeleteByRiskId((current) => {
      if (!current[riskId]) return current;
      const next = {...current};
      delete next[riskId];
      return next;
    });
  };

  const resetRiskDraftForRow = (risk: IFarmRiskCatalogueItem) => {
    const riskId = Number(risk.id);
    const selection = selectionByRiskId.get(riskId);

    setRiskDrafts((current) => ({
      ...current,
      [riskId]: {
        problem: selection?.problem ?? '',
        mitigation: selection?.mitigation ?? '',
      },
    }));

    setRiskErrors((current) => {
      if (!current[riskId]) return current;
      const next = {...current};
      delete next[riskId];
      return next;
    });

    setConfirmDeleteByRiskId((current) => {
      if (!current[riskId]) return current;
      const next = {...current};
      delete next[riskId];
      return next;
    });
  };

  const handlePersistRiskSelection = async (
    risk: IFarmRiskCatalogueItem,
    draft: IRiskDraft,
  ) => {
    if (!application?.id) {
      setTopRisksError(
        'Could not determine this application. Please refresh and try again.',
      );
      return;
    }

    const riskId = Number(risk.id);
    const trimmedProblem = draft.problem.trim();
    const trimmedMitigation = draft.mitigation.trim();

    if (!trimmedProblem || !trimmedMitigation) {
      setRiskErrors((current) => ({
        ...current,
        [riskId]:
          'Please capture both the problem and mitigation on this card.',
      }));
      return;
    }

    const selection = selectionByRiskId.get(riskId) ?? null;
    setRiskSavingState(riskId, true);

    try {
      const response = await apiCreateFdFarmRisk({
        applicationId: Number(application.id),
        riskId,
        problem: trimmedProblem,
        mitigation: trimmedMitigation,
        ...(selection?.id ? {id: Number(selection.id)} : {}),
      });

      setRiskSelections((current) => {
        const nextSelection: IFarmRiskSelection = {
          id:
            response?.id === null || response?.id === undefined
              ? selection?.id ?? null
              : Number(response.id),
          applicationId: Number(application.id),
          riskId,
          risk,
          problem: trimmedProblem,
          mitigation: trimmedMitigation,
        };

        const existingIndex = current.findIndex(
          (item) => Number(item.riskId) === riskId,
        );
        if (existingIndex === -1) {
          return [...current, nextSelection];
        }

        const next = [...current];
        next[existingIndex] = nextSelection;
        return next;
      });

      setRiskDrafts((current) => ({
        ...current,
        [riskId]: {
          problem: trimmedProblem,
          mitigation: trimmedMitigation,
        },
      }));

      setRiskErrors((current) => {
        if (!current[riskId]) return current;
        const next = {...current};
        delete next[riskId];
        return next;
      });

      setConfirmDeleteByRiskId((current) => {
        if (!current[riskId]) return current;
        const next = {...current};
        delete next[riskId];
        return next;
      });
    } catch (error) {
      console.error(
        '[ApplicationFarmInformationStep] Failed to save risk row',
        error,
      );
      setRiskErrors((current) => ({
        ...current,
        [riskId]: 'Could not save this risk. Please try again.',
      }));
    } finally {
      setRiskSavingState(riskId, false);
    }
  };

  const handleDeleteRiskSelection = async (risk: IFarmRiskCatalogueItem) => {
    const riskId = Number(risk.id);
    const selection = selectionByRiskId.get(riskId);
    if (!selection) return;

    setRiskSavingState(riskId, true);
    try {
      if (selection.id) {
        await apiDeleteFdFarmRisk(Number(selection.id));
      }

      setRiskSelections((current) =>
        current.filter((item) => Number(item.riskId) !== riskId),
      );
      setRiskDrafts((current) => ({
        ...current,
        [riskId]: lshEmptyRiskDraft(),
      }));
      setRiskErrors((current) => {
        if (!current[riskId]) return current;
        const next = {...current};
        delete next[riskId];
        return next;
      });
      setConfirmDeleteByRiskId((current) => {
        if (!current[riskId]) return current;
        const next = {...current};
        delete next[riskId];
        return next;
      });
    } catch (error) {
      console.error(
        '[ApplicationFarmInformationStep] Failed to delete risk row',
        error,
      );
      setRiskErrors((current) => ({
        ...current,
        [riskId]: 'Could not delete this risk. Please try again.',
      }));
    } finally {
      setRiskSavingState(riskId, false);
    }
  };

  const handleConfirmRiskDelete = (riskId: number) => {
    setConfirmDeleteByRiskId((current) => ({
      ...current,
      [riskId]: true,
    }));
  };

  const handleContinue = async () => {
    if (activeSubStepKey === 'commodity-selection') {
      if (selectedCommodityIds.length === 0) {
        setCommodityError('Select at least one commodity to continue.');
        return;
      }

      if (application?.id && !isSavingCommodities) {
        try {
          setCommodityError(null);
          setIsSavingCommodities(true);

          await apiCreateFdApplicationHistoryCommodities(
            selectedCommodityIds.map((commodityTypeId) => ({
              applicationId: Number(application.id),
              commodityTypeId,
            })),
          );
        } catch (error) {
          console.error(
            '[ApplicationFarmInformationStep] Failed to save commodity selections',
            error,
          );
          setCommodityError(
            'Could not save commodity selections. Please try again.',
          );
          return;
        } finally {
          setIsSavingCommodities(false);
        }
      }
    }

    if (activeSubStepKey === 'farm-overview') {
      const didSave = await handleSaveFarmOverview();
      if (!didSave) return;
    }

    if (activeSubStepKey === 'water-rights') {
      const didSave = await handleSaveWaterRightsStep();
      if (!didSave) return;
    }

    if (activeSubStepKey === 'property') {
      const didSave = await handleSavePropertyStep();
      if (!didSave) return;
    }

    if (activeSubStepKey === 'primary-customers') {
      const didSave = await handleSavePrimaryCustomersStep();
      if (!didSave) return;
    }

    if (activeSubStepKey === 'primary-suppliers') {
      const didSave = await handleSavePrimarySuppliersStep();
      if (!didSave) return;
    }

    if (activeSubStepKey === 'team') {
      const didSave = await handleSaveTeamStep();
      if (!didSave) return;
    }

    setApplication((current) => {
      if (!current) return current;

      return {
        ...current,
        progress: Math.max(Number(current.progress || 0), 4),
        subProgress: Math.max(
          Number(current.subProgress || 0),
          activeSubStepIndex + 1,
        ),
      };
    });

    goNext();
  };

  const renderCommoditySelection = () => (
    <CommoditySelectionSubStep
      commodityError={commodityError}
      isLoadingCommodities={isLoadingCommodities}
      commodityTypes={commodityTypes}
      selectedCommoditySet={selectedCommoditySet}
      isSavingCommodities={isSavingCommodities}
      onToggleCommodity={handleToggleCommodity}
    />
  );

  const renderFarmOverview = () => (
    <FarmOverviewSubStep
      farmOverviewError={farmOverviewError}
      farmValidationErrors={farmValidationErrors}
      farmBackgroundWordCount={farmBackgroundWordCount}
      farmName={farmName}
      farmBackground={farmBackground}
      farmDirections={farmDirections}
      farmOwnership={farmOwnership}
      areaLeased={areaLeased}
      leaseEndDate={leaseEndDate}
      ownedHectares={ownedHectares}
      farmType={farmType}
      areaUnderIrrigation={areaUnderIrrigation}
      areaOfDryLand={areaOfDryLand}
      selectedWaterSources={selectedWaterSources}
      waterRights={waterRights}
      fullTimeFarming={fullTimeFarming}
      shortTermGoals={shortTermGoals}
      longTermGoals={longTermGoals}
      landComposition={landComposition}
      workforceNumber={workforceNumber}
      workerTrust={workerTrust}
      employmentBeforeAfter={employmentBeforeAfter}
      onFarmNameChange={(value) => {
        setFarmName(value);
        clearFarmValidationError('farmName');
      }}
      onFarmBackgroundChange={(value) => {
        setFarmBackground(value);
        clearFarmValidationError('farmBackground');
      }}
      onFarmDirectionsChange={(value) => {
        setFarmDirections(value);
        clearFarmValidationError('farmDirections');
      }}
      onFarmOwnershipChange={(value) => {
        setFarmOwnership(value);
        clearFarmValidationError('farmOwnership');
      }}
      onOwnedHectaresChange={(value) => {
        setOwnedHectares(value);
        clearFarmValidationError('ownedHectares');
      }}
      onAreaLeasedChange={(value) => {
        setAreaLeased(value);
        clearFarmValidationError('areaLeased');
      }}
      onLeaseEndDateChange={(value) => {
        setLeaseEndDate(value);
        clearFarmValidationError('leaseEndDate');
      }}
      onFarmTypeChange={(value) => {
        setFarmType(value);
        clearFarmValidationError('farmType');
      }}
      onAreaUnderIrrigationChange={(value) => {
        setAreaUnderIrrigation(value);
        clearFarmValidationError('areaUnderIrrigation');
      }}
      onAreaOfDryLandChange={(value) => {
        setAreaOfDryLand(value);
        clearFarmValidationError('areaOfDryLand');
      }}
      onWaterSourceToggle={handleWaterSourceToggle}
      onWaterRightsChange={(value) => {
        setWaterRights(value);
        clearFarmValidationError('waterRights');
      }}
      onFullTimeFarmingChange={(value) => {
        setFullTimeFarming(value);
        clearFarmValidationError('fullTimeFarming');
      }}
      onShortTermGoalsChange={(value) => {
        setShortTermGoals(value);
        clearFarmValidationError('shortTermGoals');
      }}
      onLongTermGoalsChange={(value) => {
        setLongTermGoals(value);
        clearFarmValidationError('longTermGoals');
      }}
      onLandCompositionChange={(value) => {
        setLandComposition(value);
        clearFarmValidationError('landComposition');
      }}
      onWorkforceNumberChange={(value) => {
        setWorkforceNumber(value);
        clearFarmValidationError('workforceNumber');
      }}
      onWorkerTrustChange={(value) => {
        setWorkerTrust(value);
        clearFarmValidationError('workerTrust');
      }}
      onEmploymentBeforeAfterChange={(value) => {
        setEmploymentBeforeAfter(value);
        clearFarmValidationError('employmentBeforeAfter');
      }}
      leaseYearOptions={FARM_OVERVIEW_LEASE_YEAR_OPTIONS}
      yesNoOptions={FARM_OVERVIEW_YES_NO_OPTIONS}
      waterSourceOptions={FARM_OVERVIEW_WATER_SOURCE_OPTIONS}
    />
  );
  const isOwnedPropertyArrangement =
    lshNormalizeFarmArrangement(propertyForm.farmArrangement) === 'OWNED';

  const renderWaterRights = () => (
    <WaterRightsSubStep
      waterRightsError={waterRightsError}
      isLoadingWaterRights={isLoadingWaterRights}
      isSavingWaterRights={isSavingWaterRights}
      hasWaterRightsStepValue={hasWaterRightsStepValue}
      setHasWaterRightsStepValue={setHasWaterRightsStepValue}
      selectedWaterSource={selectedWaterSource}
      selectedHouseholdSource={selectedHouseholdSource}
      waterRightForm={waterRightForm}
      setWaterRightForm={setWaterRightForm}
      waterRightsRecords={waterRightsRecords}
      onWaterRightFormChange={handleWaterRightFormChange}
      onAddWaterRightRecord={handleAddWaterRightRecord}
      onRemoveWaterRightRecord={handleRemoveWaterRightRecord}
      yesNoOptions={WATER_RIGHTS_YES_NO_OPTIONS}
      sourceOptions={WATER_RIGHTS_SOURCE_OPTIONS}
      scaleOptions={WATER_RIGHTS_SCALE_OPTIONS}
    />
  );

  const renderProperty = () => (
    <PropertySubStep
      propertyError={propertyError}
      isLoadingProperties={isLoadingProperties}
      isSavingProperties={isSavingProperties}
      propertyForm={propertyForm}
      isOwnedPropertyArrangement={isOwnedPropertyArrangement}
      propertyRecords={propertyRecords}
      onPropertyFormChange={handlePropertyFormChange}
      onAddPropertyRecord={handleAddPropertyRecord}
      onRemovePropertyRecord={handleRemovePropertyRecord}
      onNormalizeFarmArrangement={lshNormalizeFarmArrangement}
    />
  );

  const renderTopRisks = () => (
    <TopRisksSubStep
      topRisksError={topRisksError}
      isLoadingTopRisks={isLoadingTopRisks}
      selectedRiskCount={selectedRiskCount}
      sortedRiskCatalogue={sortedRiskCatalogue}
      selectionByRiskId={selectionByRiskId}
      perRiskSaving={perRiskSaving}
      riskDrafts={riskDrafts}
      confirmDeleteByRiskId={confirmDeleteByRiskId}
      riskErrors={riskErrors}
      hasAttemptedTopRisksBootstrap={hasAttemptedTopRisksBootstrap}
      hasApplicationId={Boolean(application?.id)}
      onRiskDraftChange={handleRiskDraftChange}
      onDeleteRiskSelection={handleDeleteRiskSelection}
      onConfirmDeleteRisk={handleConfirmRiskDelete}
      onPersistRiskSelection={handlePersistRiskSelection}
      onResetRiskDraftForRow={resetRiskDraftForRow}
    />
  );

  const renderCustomers = () => (
    <PrimaryCustomersSubStep
      isLoadingPrimaryCustomers={isLoadingPrimaryCustomers}
      totalPrimaryCustomerSelections={totalPrimaryCustomerSelections}
      primaryCustomerDeleteQueueLength={primaryCustomerDeleteQueue.length}
      sortedPrimaryCustomers={sortedPrimaryCustomers}
      expandedPrimaryCustomerId={expandedPrimaryCustomerId}
      primaryCustomerSelectionMap={primaryCustomerSelectionMap}
      sortedPrimaryCustomerCrops={sortedPrimaryCustomerCrops}
      isSavingPrimaryCustomers={isSavingPrimaryCustomers}
      primaryCustomersError={primaryCustomersError}
      hasApplicationId={Boolean(application?.id)}
      onTogglePrimaryCustomer={handleTogglePrimaryCustomer}
      onSelectPrimaryCustomerCrop={handleSelectPrimaryCustomerCrop}
      onDeselectPrimaryCustomerCrop={handleDeselectPrimaryCustomerCrop}
      isPrimaryCustomerItemQueuedForDelete={
        isPrimaryCustomerItemQueuedForDelete
      }
    />
  );

  const renderSuppliers = () => (
    <PrimarySuppliersSubStep
      isLoadingPrimarySuppliers={isLoadingPrimarySuppliers}
      totalPrimarySupplierSelections={totalPrimarySupplierSelections}
      primarySupplierDeleteQueueLength={primarySupplierDeleteQueue.length}
      sortedPrimarySuppliers={sortedPrimarySuppliers}
      expandedPrimarySupplierId={expandedPrimarySupplierId}
      primarySupplierSelectionMap={primarySupplierSelectionMap}
      isSavingPrimarySuppliers={isSavingPrimarySuppliers}
      primarySuppliersError={primarySuppliersError}
      hasApplicationId={Boolean(application?.id)}
      onTogglePrimarySupplier={handleTogglePrimarySupplier}
      onSelectPrimarySupplierProduct={handleSelectPrimarySupplierProduct}
      onDeselectPrimarySupplierProduct={handleDeselectPrimarySupplierProduct}
      isPrimarySupplierItemQueuedForDelete={
        isPrimarySupplierItemQueuedForDelete
      }
    />
  );

  const renderTeam = () => (
    <TeamSubStep
      isLoadingTeam={isLoadingTeam}
      preparedTeamMembers={preparedTeamMembers}
      teamDeleteLoading={teamDeleteLoading}
      pendingTeamDeleteId={pendingTeamDeleteId}
      teamModalState={teamModalState}
      teamRoles={teamRoles}
      teamError={teamError}
      hasApplicationId={Boolean(application?.id)}
      teamNameRequiredError={TEAM_NAME_REQUIRED_ERROR}
      teamRoleRequiredError={TEAM_ROLE_REQUIRED_ERROR}
      teamRoleNotAvailableError={TEAM_ROLE_NOT_AVAILABLE_ERROR}
      onOpenTeamModal={openTeamModal}
      onCloseTeamModal={closeTeamModal}
      onTeamModalSubmit={handleTeamModalSubmit}
      onRequestDeleteTeamMember={requestDeleteTeamMember}
      onTeamModalNameChange={handleTeamModalNameChange}
      onTeamModalRoleChange={handleTeamModalRoleChange}
      onTeamModalDescriptionChange={handleTeamModalDescriptionChange}
    />
  );

  const renderSubStepContent = () => {
    if (activeSubStepKey === 'commodity-selection') {
      return renderCommoditySelection();
    }

    if (activeSubStepKey === 'farm-overview') {
      return renderFarmOverview();
    }

    if (activeSubStepKey === 'water-rights') {
      return renderWaterRights();
    }

    if (activeSubStepKey === 'property') {
      return renderProperty();
    }

    if (activeSubStepKey === 'top-risks') {
      return renderTopRisks();
    }

    if (activeSubStepKey === 'primary-customers') {
      return renderCustomers();
    }

    if (activeSubStepKey === 'primary-suppliers') {
      return renderSuppliers();
    }

    return renderTeam();
  };

  return (
    <>
      <ApplicationJourneySubStepProgress
        steps={farmSubSteps.map(({key, title}) => ({key, title}))}
        activeIndex={activeSubStepIndex}
        ariaLabel="Farm information sub-steps"
        onStepClick={goToSubStep}
      />
      <ApplicationJourneyStepLayout
        title={activeSubStep?.title || activeStep.title}
        description={activeSubStep?.description || activeStep.description}
        secondaryAction={{
          label: 'Back',
          onClick: goPrevious,
        }}
        primaryAction={{
          label:
            activeSubStepKey === 'commodity-selection'
              ? isSavingCommodities
                ? 'Saving...'
                : 'Save & continue'
              : activeSubStepKey === 'farm-overview'
              ? isSavingFarmOverview
                ? 'Saving...'
                : 'Save & continue'
              : activeSubStepKey === 'water-rights'
              ? isSavingWaterRights
                ? 'Saving...'
                : 'Save & continue'
              : activeSubStepKey === 'property'
              ? isSavingProperties
                ? 'Saving...'
                : 'Save & continue'
              : activeSubStepKey === 'top-risks'
              ? 'Save & continue'
              : activeSubStepKey === 'primary-customers'
              ? isSavingPrimaryCustomers
                ? 'Saving...'
                : 'Save & continue'
              : activeSubStepKey === 'primary-suppliers'
              ? isSavingPrimarySuppliers
                ? 'Saving...'
                : 'Save & continue'
              : activeSubStepKey === 'team'
              ? isSavingTeam
                ? 'Saving...'
                : 'Save & continue'
              : activeSubStepIndex === farmSubSteps.length - 1
              ? 'Continue to production info'
              : 'Continue',
          onClick: handleContinue,
          disabled:
            activeSubStepKey === 'commodity-selection'
              ? isLoadingCommodities || isSavingCommodities
              : activeSubStepKey === 'farm-overview'
              ? isSavingFarmOverview
              : activeSubStepKey === 'water-rights'
              ? isLoadingWaterRights || isSavingWaterRights
              : activeSubStepKey === 'property'
              ? isLoadingProperties || isSavingProperties
              : activeSubStepKey === 'top-risks'
              ? isLoadingTopRisks || anyRiskSaving
              : activeSubStepKey === 'primary-customers'
              ? isLoadingPrimaryCustomers ||
                isSavingPrimaryCustomers ||
                !application?.id
              : activeSubStepKey === 'primary-suppliers'
              ? isLoadingPrimarySuppliers ||
                isSavingPrimarySuppliers ||
                !application?.id
              : activeSubStepKey === 'team'
              ? isLoadingTeam || isSavingTeam || !application?.id
              : false,
          loading:
            activeSubStepKey === 'commodity-selection'
              ? isSavingCommodities
              : activeSubStepKey === 'farm-overview'
              ? isSavingFarmOverview
              : activeSubStepKey === 'water-rights'
              ? isSavingWaterRights
              : activeSubStepKey === 'property'
              ? isSavingProperties
              : activeSubStepKey === 'top-risks'
              ? false
              : activeSubStepKey === 'primary-customers'
              ? isSavingPrimaryCustomers
              : activeSubStepKey === 'primary-suppliers'
              ? isSavingPrimarySuppliers
              : activeSubStepKey === 'team'
              ? isSavingTeam
              : false,
        }}
      >
        {renderSubStepContent()}
      </ApplicationJourneyStepLayout>
    </>
  );
};

export default ApplicationFarmInformationStep;
